<script lang="ts">
  import MediaCredit from '../MediaCredit.svelte'
  import VideoPlayer from '../VideoPlayer.svelte'
  import { getI18n } from '../../i18n'
  import { getPageCategory } from '../../pageCategory'
  import { formatDuration } from '../../utils/duration'
  import type { SerializedVideoGalleryBlock } from '../../types'

  let { block, priority = false }: { block: SerializedVideoGalleryBlock; priority?: boolean } = $props()

  const i18n = getI18n()
  const pageCategory = getPageCategory()
  const headingId = $derived(`video-gallery-${block.id}`)

  let selectedIndex = $state(0)
  let pickedFromPlaylist = $state(false)
  let playerElement = $state<HTMLElement>()

  const selectedVideo = $derived(block.videos[selectedIndex] ?? block.videos[0])
  const summary = $derived.by(() => {
    const count = block.videos.length
    const minutes = Math.max(1, Math.round(block.videos.reduce((total, video) => total + video.duration, 0) / 60))
    return count === 1 ? i18n.t('videoGallery.summaryOne', { minutes }) : i18n.t('videoGallery.summaryOther', { count, minutes })
  })

  function select(index: number) {
    selectedIndex = index
    pickedFromPlaylist = true
    playerElement?.scrollIntoView({ block: 'nearest' })
  }
</script>

<figure class="video-gallery" aria-labelledby={headingId}>
  <div class="video-gallery-player" bind:this={playerElement}>
    {#key selectedVideo.id}
      <VideoPlayer video={selectedVideo} autoplay={pickedFromPlaylist} priority={priority && !pickedFromPlaylist} />
    {/key}
    <MediaCredit />
  </div>

  <div class="video-gallery-panel">
    <div class="video-gallery-header" id={headingId}>
      <span class="video-gallery-heading">{block.caption}</span>
      <span class="video-gallery-summary">{summary}</span>
    </div>

    {#if block.videos.length > 1}
      <ol class="video-gallery-playlist">
        {#each block.videos as video, index (video.id)}
          <li class="video-gallery-entry">
            <button
              type="button"
              class={['video-gallery-item', {
                '-active': index === selectedIndex,
                '-variant-brand': pageCategory.variant === 'brand',
                '-variant-warning': pageCategory.variant === 'warning',
                '-variant-success': pageCategory.variant === 'success',
              }]}
              aria-current={index === selectedIndex ? 'true' : undefined}
              onclick={() => select(index)}
            >
              <span class="video-gallery-thumbnail">
                <img class="video-gallery-thumbnail-image" src={video.poster.src} srcset={video.poster.srcset} sizes="96px" width={video.poster.width} height={video.poster.height} alt="" loading="lazy" />
                <span class="video-gallery-duration">{formatDuration(video.duration)}</span>
              </span>
              <span class="video-gallery-title">{video.title}</span>
            </button>
          </li>
        {/each}
      </ol>
    {/if}
  </div>
</figure>

<style>
  .video-gallery {
    grid-column: full;
    display: grid;
    grid-template-columns: subgrid;
    row-gap: var(--space-5);
  }

  .video-gallery-player {
    grid-column: content;
    position: relative;
  }

  .video-gallery-panel {
    grid-column: content;
    display: grid;
    gap: var(--space-4);
  }

  @media (--mobile) {
    .video-gallery-player {
      grid-column: full;
    }
  }

  .video-gallery-header {
    display: grid;
    gap: var(--space-1);
    padding-inline-start: calc(var(--space-4) + var(--border-2));
  }

  .video-gallery-heading {
    font-size: var(--font-size-5);
    font-weight: var(--font-weight-3);
    line-height: var(--line-height-2);
    color: var(--color-heading);
    text-wrap: balance;
  }

  .video-gallery-summary {
    font-size: var(--font-size-3);
    color: var(--color-text-muted);
    font-variant-numeric: tabular-nums;
  }

  .video-gallery-playlist {
    display: grid;
    gap: var(--space-1);
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .video-gallery-entry {
    display: block;
  }

  .video-gallery-item {
    display: grid;
    grid-template-columns: var(--space-23) minmax(0, 1fr);
    gap: var(--space-5);
    align-items: center;
    width: 100%;
    padding: var(--space-3) var(--space-4);
    border: none;
    border-inline-start: var(--border-2) solid transparent;
    border-start-end-radius: var(--radius-2);
    border-end-end-radius: var(--radius-2);
    background: none;
    color: var(--color-heading);
    font: inherit;
    text-align: start;
    cursor: pointer;
    transition: background-color var(--transition-medium);

    &:hover {
      background-color: var(--color-gray-95);
    }
  }

  .video-gallery-item.-active.-variant-brand {
    background-color: var(--color-variant-brand-muted);
    border-inline-start-color: var(--color-variant-brand);
    color: var(--color-variant-brand);
  }

  .video-gallery-item.-active.-variant-warning {
    background-color: var(--color-variant-warning-muted);
    border-inline-start-color: var(--color-variant-warning);
    color: var(--color-variant-warning);
  }

  .video-gallery-item.-active.-variant-success {
    background-color: var(--color-variant-success-muted);
    border-inline-start-color: var(--color-variant-success);
    color: var(--color-variant-success);
  }

  .video-gallery-thumbnail {
    position: relative;
    display: block;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border-radius: var(--radius-1);
    background-color: var(--color-gray-95);
  }

  .video-gallery-thumbnail-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .video-gallery-duration {
    position: absolute;
    inset-block-end: var(--space-1);
    inset-inline-end: var(--space-1);
    padding: 0 var(--space-2);
    border-radius: var(--radius-1);
    background-color: var(--shadow-2);
    color: var(--color-white);
    font-size: var(--font-size-2);
    font-variant-numeric: tabular-nums;
  }

  .video-gallery-title {
    font-size: var(--font-size-4);
    font-weight: var(--font-weight-3);
    line-height: var(--line-height-3);
  }
</style>
